import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { WorkOrderItem, WorkOrderItemStatus } from '../../types';

interface WorkOrderItemsTableProps {
  items: WorkOrderItem[];
  onAssignSerial?: (item: WorkOrderItem) => void;
  onRemoveItem?: (item: WorkOrderItem) => void;
  readOnly?: boolean;
}

const itemStatusConfig: Record<WorkOrderItemStatus, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-gray-500 hover:bg-gray-600 text-white' },
  allocated: { label: 'Allocated', className: 'bg-blue-500 hover:bg-blue-600 text-white' },
  picking: { label: 'Picking', className: 'bg-purple-500 hover:bg-purple-600 text-white' },
  picked: { label: 'Picked', className: 'bg-indigo-500 hover:bg-indigo-600 text-white' },
  shipped: { label: 'Shipped', className: 'bg-green-500 hover:bg-green-600 text-white' },
  cancelled: { label: 'Cancelled', className: 'bg-red-500 hover:bg-red-600 text-white' },
};

function ItemStatusBadge({ status }: { status: WorkOrderItemStatus }) {
  const config = itemStatusConfig[status] || { label: status, className: 'bg-gray-500' };
  return <Badge className={config.className}>{config.label}</Badge>;
}

export function WorkOrderItemsTable({
  items,
  onAssignSerial,
  onRemoveItem,
  readOnly,
}: WorkOrderItemsTableProps) {
  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No items in this work order yet. Add items to begin fulfillment.
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>SKU</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="text-right">Ordered</TableHead>
            <TableHead className="text-right">Allocated</TableHead>
            <TableHead className="text-right">Picked</TableHead>
            <TableHead className="text-right">Shipped</TableHead>
            <TableHead>Status</TableHead>
            {!readOnly && <TableHead className="w-[160px]"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const remaining = item.quantity_ordered - (item.quantity_allocated || 0);
            const canAssign = remaining > 0 && item.item_status !== 'cancelled' && item.item_status !== 'shipped';

            return (
              <TableRow key={item.id}>
                <TableCell className="font-mono text-sm">{item.sku_code}</TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {item.sku_name || '-'}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {item.quantity_ordered}
                </TableCell>
                <TableCell className="text-right">
                  <span className={item.quantity_allocated >= item.quantity_ordered ? 'text-green-600' : ''}>
                    {item.quantity_allocated || 0}
                  </span>
                  {remaining > 0 && (
                    <span className="text-muted-foreground text-xs ml-1">
                      ({remaining} left)
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {item.quantity_picked || 0}
                </TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {item.quantity_shipped || 0}
                </TableCell>
                <TableCell>
                  <ItemStatusBadge status={item.item_status} />
                </TableCell>
                {!readOnly && (
                  <TableCell>
                    <div className="flex gap-1">
                      {canAssign && onAssignSerial && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onAssignSerial(item)}
                        >
                          Assign Serial
                        </Button>
                      )}
                      {item.item_status === 'pending' && item.quantity_allocated === 0 && onRemoveItem && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => onRemoveItem(item)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
